import { Box, Button } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ManageBackupsInfo } from "IF/interfaceType";
import axios from "axios";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import { MessageModal } from "./MessageModal";
import ConfirmationModal from "./ConfirmationModal";
import { DataContext } from "Contexts/DataProvider";
import ManageBackupsTable from "./ManageBackupsTable";
import ManageBackupsUploadDialog from "./ManageBackupsUploadDialog";
import { createTitle } from "Utils/format";
import ManageBackupsParts from "./ManageBackupsParts";

export const ManageBackupsPage = () => {

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const {setChoiceMenu, toggleMenu } = sidebarContext;

  const [data, setData] = useState<ManageBackupsInfo[]>([]);
  // 保管中のバックアップデータ合計サイズ
  const [totalBackupSize, setTotalBackupSize] = useState("");

  const [isCheck, setIsCheck] = useState(false);

  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [tableBodyWidth, setTableBodyWidth] = useState(0);

  const disabledButtonStyle = {
    color: "white",
    opacity: 0.6,
  };

  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<React.ReactNode>("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);

  const handleConfirmModalOpen = (
    message: React.ReactNode,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // タイトル作成
  const title = "バックアップデータ管理";
  useEffect(() => {
    document.title = createTitle(title);
    setChoiceMenu(title);
    toggleMenu("YKC専用");
  }, [setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      let windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth - 52;
      const width = window.innerWidth;
      if (width < 600) {
        windowHeight = windowHeight - 200; // xs
      } else if (width < 900) {
        windowHeight = windowHeight - 210; // sm
      } else if (width < 1200) {
        windowHeight = windowHeight - 210; // md
      } else {
        windowHeight = windowHeight - 165; // lg
      }

      setTableBodyHeight(windowHeight);
      setTableBodyWidth(windowWidth);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // 初期表示データ取得
    getData();

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const hasCheckedItems = data.some((item) => item.check);
    setIsCheck(hasCheckedItems);
  }, [data]);

  // バックアップデータ取得処理
  const getData = async () => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/ManageBackups/GetBackupList`
      );
      setData(response.data.manageBackupsInfo);
      setTotalBackupSize(response.data.totalFileSize);
    } catch (error) {
      setModalOpen(true);
      setMessage("バックアップデータの取得に失敗しました");
      console.error("バックアップデータの取得に失敗しました:", error);
    }
  };

  // チェックボックスの状態が変更されたときの処理
  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const { checked } = event.target;
    setData((prevData) =>
      prevData.map((row) => (row.id === id ? { ...row, check: checked } : row))
    );
  };

  // ダウンロード処理
  const handleDownloadChecked = async () => {
    const checkedFiles = data
      .filter((item) => item.check)
      .map((item) => item.fileName);

    for (const fileName of checkedFiles) {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/ManageBackups/Download?fileName=${fileName}`,
          {
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(
          `ファイル「${fileName}」のダウンロードに失敗しました`,
          error
        );
        setModalOpen(true);
        setMessage(`ファイル「${fileName}」のダウンロードに失敗しました`);
      }
    }
  };

  // 削除処理
  const handleDeleteChecked = async () => {
    const checkedFiles = data
      .filter((item) => item.check)
      .map((item) => item.fileName);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/ManageBackups/DeleteFiles`,
        checkedFiles
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("選択したバックアップデータを削除しました。");
        getData();
      } else {
        throw new Error(
          `バックアップデータの削除に失敗しました。サーバーからの応答: ${response.status} ${response.statusText}`
        );
      }
    } catch (error: any) {
      let errorMessage = `${error.message}`;
      if (error.response?.data?.failedFiles) {
        errorMessage += `\n【失敗したファイル】\n${error.response.data.failedFiles.join("\n")}`;
      }
      setModalOpen(true);
      setMessage(errorMessage);
      console.error("Status code:", error.response.status);
      console.error("Message:", error.response.data);
    }
  };

  // アップロード
  const handleOpen = () => setUploadOpen(true);
  const handleUploadClose = () => {
    setUploadOpen(false);
    setSelectedFiles([]);
  };
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setSelectedFiles(Array.from(files));
    }
  };

  const handleUpload = async () => {
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/ManageBackups/UploadFiles`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("ファイルをアップロードしました。");
        setSelectedFiles([]); // アップロード完了後に選択ファイルをリセット
        if (fileInputRef.current) {
          fileInputRef.current.value = ""; // ファイルインプットの値をリセット
        }
        getData();
      } else {
        throw new Error(
          `ファイルのアップロードに失敗しました。サーバーからの応答: ${response.status} ${response.statusText}`
        );
      }
    } catch (error: any) {
      let errorMessage = `${error.response.data.message}`;
      if (error.response?.data?.failedFiles) {
        errorMessage += `\n【失敗したファイル】\n${error.response.data.failedFiles.join("\n")}`;
      }
      setModalOpen(true);
      setMessage(errorMessage);
      console.error("Status code:", error.response.status);
      console.error("Message:", error.response.data);
    }
  };

  const handleOpenFileExplorer = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 全てチェック/チェック解除のハンドラ
  const handleCheckAll = (
    checkedData: ManageBackupsInfo[],
    checked: boolean
  ) => {
    const targetIds = new Set(checkedData.map((row) => row.id));

    const updated = data.map((row) =>
      targetIds.has(row.id) ? { ...row, check: checked } : row
    );

    setData(updated);
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      <ManageBackupsUploadDialog
        open={uploadOpen}
        handleClose={handleUploadClose}
        fileInputRef={fileInputRef}
        selectedFiles={selectedFiles}
        handleFileChange={handleFileChange}
        handleOpenFileExplorer={handleOpenFileExplorer}
        handleUpload={handleUpload}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            lg: "row",
          },
          gap: {
            xs: 1,
            lg: 0.5,
          },
          marginBottom: 0,
          marginTop: 0.1,
          paddingTop: 0,
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: {
              xs: 1,
              lg: 2,
            },
            alignItems: "center",
            justifyContent: "start",
          }}
        >
          <Button
            variant="contained"
            onClick={handleOpen}
            sx={{
              width: {
                xs: "100%",
                md: "20vh",
              },
            }}
          >
            アップロード
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#000080" }}
            sx={{
              width: {
                xs: "100%",
                md: "20vh",
              },
              "&.Mui-disabled": disabledButtonStyle,
            }}
            disabled={!isCheck}
            onClick={handleDownloadChecked}
          >
            ダウンロード
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#DC143C" }}
            sx={{
              width: {
                xs: "100%",
                md: "20vh",
              },
              "&.Mui-disabled": disabledButtonStyle,
            }}
            disabled={!isCheck}
            onClick={() =>
              handleConfirmModalOpen("選択されたファイルを削除しますか？", () =>
                handleDeleteChecked()
              )
            }
          >
            削除
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            marginBottom: 0.5,
          }}
        >
          <ManageBackupsParts
            fileNum={data.length}
            fileSize={totalBackupSize}
          />
        </Box>
      </Box>
      <ManageBackupsTable
        data={data}
        tableBodyHeight={tableBodyHeight}
        tableBodyWidth={tableBodyWidth}
        handleCheckboxChange={handleCheckboxChange}
        handleCheckAll={handleCheckAll}
      />
    </>
  );
};
export default ManageBackupsPage;
